import * as React from 'react';
import { Copy, Download, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableMultiSelectFilter,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  ConfirmDialog,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { downloadCsv } from '../../../../../lib/downloadBlob';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useAdsTxtMutations, useAdsTxtRecordsQuery } from '../../hooks';
import {
  adsTxtPageSizes,
  adsTxtRecordLine,
  AdsTxtRecordType,
  type AdsTxtRecord,
} from '../../model';
import { useAdsTxtRecordsRouteFilters } from '../../useAdsTxtRecordsRouteFilters';
import { AdsTxtRecordDialog } from '../AdsTxtRecordDialog';
import { copyToClipboard } from '../../../../../lib/clipboard';

export function AdsTxtRecordsTable() {
  const { can } = useShigoPermissions();
  const routeFilters = useAdsTxtRecordsRouteFilters();
  const { filter } = routeFilters;
  const query = useAdsTxtRecordsQuery(filter);
  const mutations = useAdsTxtMutations();
  const search = useDebouncedValue(filter.searchTerm, routeFilters.setSearch, 650);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());
  const [editor, setEditor] = React.useState<AdsTxtRecord | 'create' | null>(null);
  const [deleting, setDeleting] = React.useState<AdsTxtRecord | null>(null);
  const rows = query.data?.content ?? [];
  const selectedRows = rows.filter((row) => selectedIds.has(row.id));
  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
  let selectionState: boolean | 'indeterminate' = false;
  if (selectedRows.length > 0) selectionState = 'indeterminate';
  if (allSelected) selectionState = true;
  const canCreate = can(ShigoPermission.SupplyAdsTxtRecordsCreate);
  const canEdit = can(ShigoPermission.SupplyAdsTxtRecordsEdit);
  const canCopy = can(ShigoPermission.SupplyAdsTxtRecordsCopy);
  const canDelete = can(ShigoPermission.SupplyAdsTxtRecordsDelete);
  const columns = buildDashboardDataTableColumns<AdsTxtRecord>([
    {
      id: 'select',
      kind: DashboardDataTableColumnKind.Selection,
      headerChecked: selectionState,
      headerLabel: dashboardT('adsTxt.records.selectAll'),
      onHeaderChange: (checked) => {
        setSelectedIds(checked ? new Set(rows.map((row) => row.id)) : new Set());
      },
      checked: (record) => selectedIds.has(record.id),
      label: (record) => dashboardT('adsTxt.records.selectRecord', { domain: record.domain }),
      onChange: (record, checked) => {
        setSelectedIds((current) => toggleId(current, record.id, checked));
      },
      className: 'w-12',
      headerClassName: 'w-12',
    },
    textColumn('domain', 'adsTxt.records.domain', (record) => record.domain, 'w-48'),
    textColumn(
      'accountId',
      'adsTxt.records.accountId',
      (record) => record.publisherAccountId,
      'w-40',
    ),
    textColumn('accountType', 'adsTxt.records.accountType', (record) => record.accountType, 'w-32'),
    textColumn(
      'certAuthority',
      'adsTxt.records.certAuthority',
      (record) => record.certAuthority ?? '—',
      'w-44',
    ),
    infoColumn('comment', 'adsTxt.records.comment', (record) => record.comment),
    textColumn(
      'demandPartner',
      'adsTxt.records.demandPartner',
      (record) =>
        record.demandPartner?.displayName ??
        record.demandPartner?.name ??
        record.demandPartnerName ??
        '—',
      'w-44',
    ),
    textColumn('priority', 'adsTxt.records.priority', (record) => record.priority ?? '—', 'w-24'),
    boolColumn('web', 'Web', (record) => record.web ?? false),
    boolColumn('ctv', 'CTV', (record) => record.ctv ?? false),
    infoColumn('note', 'adsTxt.records.note', (record) => record.note),
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      className: 'w-14 text-right',
      headerClassName: 'w-14',
      cell: (record) => (
        <DashboardTableRowActions label={dashboardT('adsTxt.records.actions')}>
          {canEdit ? (
            <DashboardTableRowActionItem
              onClick={() => {
                setEditor(record);
              }}
            >
              <Pencil className="h-4 w-4" />
              {dashboardT('adsTxt.edit')}
            </DashboardTableRowActionItem>
          ) : null}
          {canCopy ? (
            <DashboardTableRowActionItem onClick={() => void copyRecords([record])}>
              <Copy className="h-4 w-4" />
              {dashboardT('adsTxt.copy')}
            </DashboardTableRowActionItem>
          ) : null}
          {canDelete ? (
            <DashboardTableRowActionItem
              className="text-destructive"
              onClick={() => {
                setDeleting(record);
              }}
            >
              <Trash2 className="h-4 w-4" />
              {dashboardT('adsTxt.delete')}
            </DashboardTableRowActionItem>
          ) : null}
        </DashboardTableRowActions>
      ),
    },
  ]);

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <DashboardDataTable
        title={dashboardT('adsTxt.records.title')}
        tableLabel={dashboardT('adsTxt.records.title')}
        columns={columns}
        items={rows}
        getRowKey={(record) => record.id}
        loading={query.isLoading}
        loadingMessage={dashboardT('adsTxt.records.loading')}
        errorMessage={query.isError ? dashboardT('adsTxt.records.loadError') : undefined}
        emptyMessage={dashboardT('adsTxt.records.empty')}
        minWidthClassName="min-w-[1480px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('adsTxt.records.search')}
              placeholder={dashboardT('adsTxt.records.search')}
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <DashboardTableMultiSelectFilter
              ariaLabel={dashboardT('adsTxt.records.typeFilter')}
              label={
                filter.types.length
                  ? filter.types.join(', ')
                  : dashboardT('adsTxt.records.allTypes')
              }
              value={filter.types}
              options={Object.values(AdsTxtRecordType).map((value) => ({ value, label: value }))}
              onChange={routeFilters.setTypes}
            />
            {selectedRows.length && canCopy ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => void copyRecords(selectedRows)}
              >
                <Copy className="h-4 w-4" />
                {dashboardT('adsTxt.records.copySelected', { count: selectedRows.length })}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={dashboardT('adsTxt.refresh')}
              onClick={() => void query.refetch()}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {can(ShigoPermission.SupplyAdsTxtRecordsExport) ? (
              <Button
                type="button"
                variant="outline"
                disabled={mutations.exportRecords.isPending}
                onClick={() =>
                  void mutations.exportRecords
                    .mutateAsync(filter)
                    .then((csv) => {
                      downloadCsv(csv, 'adstxt-records.csv');
                    })
                    .catch((error: unknown) => {
                      showRequestError(error, dashboardT('adsTxt.records.exportError'));
                    })
                }
              >
                <Download className="h-4 w-4" />
                {dashboardT('adsTxt.export')}
              </Button>
            ) : null}
            {canCreate ? (
              <Button
                type="button"
                onClick={() => {
                  setEditor('create');
                }}
              >
                <Plus className="h-4 w-4" />
                {dashboardT('adsTxt.records.add')}
              </Button>
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: adsTxtPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            routeFilters.setPagination(next.page, next.pageSize);
          },
        }}
      />
      {editor ? (
        <AdsTxtRecordDialog
          record={editor === 'create' ? null : editor}
          onClose={() => {
            setEditor(null);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={dashboardT('adsTxt.records.deleteTitle')}
        description={dashboardT('adsTxt.records.deleteDescription', {
          domain: deleting?.domain ?? '',
        })}
        cancelLabel={dashboardT('adsTxt.cancel')}
        confirmLabel={dashboardT('adsTxt.delete')}
        pending={mutations.deleteRecord.isPending}
        destructive
        onConfirm={() => {
          if (!deleting) return;
          void mutations.deleteRecord
            .mutateAsync(deleting.id)
            .then(() => {
              setDeleting(null);
              setSelectedIds(new Set());
              toast.success(dashboardT('adsTxt.records.deleted'));
            })
            .catch((error: unknown) => {
              showRequestError(error, dashboardT('adsTxt.records.deleteError'));
            });
        }}
      />
    </main>
  );
}

function textColumn(
  id: string,
  key: Parameters<typeof dashboardT>[0],
  value: (record: AdsTxtRecord) => React.ReactNode,
  width: string,
) {
  return {
    id,
    kind: DashboardDataTableColumnKind.Text as const,
    header: dashboardT(key),
    headerClassName: width,
    className: width,
    value,
  };
}
function boolColumn(id: string, header: string, value: (record: AdsTxtRecord) => boolean) {
  return {
    id,
    kind: DashboardDataTableColumnKind.Custom as const,
    header,
    headerClassName: 'w-20 text-center',
    className: 'w-20 text-center',
    cell: (record: AdsTxtRecord) =>
      value(record) ? (
        <span className="text-[var(--status-success-text)]">✓</span>
      ) : (
        <span className="text-muted-foreground">✕</span>
      ),
  };
}
function infoColumn(
  id: string,
  key: Parameters<typeof dashboardT>[0],
  value: (record: AdsTxtRecord) => string | null | undefined,
) {
  return {
    id,
    kind: DashboardDataTableColumnKind.Custom as const,
    header: dashboardT(key),
    headerClassName: 'w-24 text-center',
    className: 'w-24 text-center',
    cell: (record: AdsTxtRecord) =>
      value(record) ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help">ⓘ</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{value(record)}</TooltipContent>
        </Tooltip>
      ) : (
        '—'
      ),
  };
}
function toggleId(current: Set<string>, id: string, checked: boolean) {
  const next = new Set(current);
  if (checked) next.add(id);
  else next.delete(id);
  return next;
}
function copyRecords(records: AdsTxtRecord[]) {
  return copyToClipboard(records.map(adsTxtRecordLine).join('\n'), {
    successMessage: dashboardT('adsTxt.records.copied'),
  });
}
